<script module lang="ts">
    import type { HTMLInputAttributes } from 'svelte/elements';
    import type { Field } from '@/types/types';
    import type { CalendarDate, ZonedDateTime } from '@internationalized/date';
    import { fromDate } from '@internationalized/date';

    export type Props = {
        label?: string;
        error?: null | string;
        value?: CalendarDateTime;
        class?: string;
        args?: HTMLInputAttributes;
        minValue?: CalendarDateTime;
        maxValue?: CalendarDateTime;
        showValidation?: boolean;
        field?: Field;
    };

    export const dateToInternal = (date: Date | string) => {
        if (typeof date === 'string') date = new Date(date);
        return fromDate(date, getLocalTimeZone());
    };
    export const internalToDate = (calDate: CalendarDate | ZonedDateTime | Date | string) => {
        if (typeof calDate === 'string') return new Date(calDate);
        if (calDate instanceof Date) return calDate;
        return calDate.toDate(getLocalTimeZone());
    };
</script>

<script lang="ts">
    import CalendarIcon from '@lucide/svelte/icons/calendar';
    import {
        DateFormatter,
        getLocalTimeZone,
        parseDateTime,
        CalendarDateTime,
    } from '@internationalized/date';
    import { cn } from '@/lib/utils.js';
    import { buttonVariants } from '@/components/ui/button/index.js';
    import { Calendar } from '@/components/ui/calendar/index.js';
    import * as Popover from '@/components/ui/popover/index.js';
    import InputError from '@/components/InputError.svelte';
    import { Label } from '@/components/ui/label';
    import Time from './Time.svelte';
    import { createFieldValidation, isFieldRequired } from '@/lib/fieldValidation';

    let { label, value = $bindable(), error, minValue, maxValue, showValidation = false, field, ...args }: Props = $props();

    if (typeof value === 'string') {
        value = parseDateTime(value);
    }

    const df = new DateFormatter('en-UK', { dateStyle: 'long', timeStyle: 'short' });

    let isDirty = $state(false);

    const computedLabel = $derived(field?.label ?? label);
    const computedRequired = $derived(isFieldRequired(field, args.required));

    // Get validation state
    const validation = $derived.by(() => {
        return createFieldValidation({
            field,
            ui_type: field?.ui_type ?? 'datetime',
            input_type: field?.input_type,
            value: value ? 'filled' : '', // Has value or not
            isDirty,
            required: computedRequired
        });
    });

    // Compute validation classes - combine showValidation mode with standard validation
    const validationClasses = $derived(() => {
        // If there's a server error, always show error state
        if (error) {
            return 'input-error';
        }
        // In showValidation mode, show success if valid
        if (showValidation && value) {
            return 'input-success';
        }
        // Otherwise use standard validation classes
        return validation.classes;
    });

    const isEmptyRequired = $derived(computedRequired && !value);

    // Mark as dirty when popover opens
    function handleOpenChange(open: boolean) {
        if (open) {
            isDirty = true;
        }
    }
</script>

<div class="grid gap-2">
    <Label for={computedLabel}>
        {computedLabel}
        {#if validation.isRequired}
            <span class="ml-1 text-red-500">*</span>
        {/if}
    </Label>

    <Popover.Root onOpenChange={handleOpenChange}>
        <Popover.Trigger
            aria-invalid={error || isEmptyRequired ? true : undefined}
            data-dont-unselect="true"
            class={cn(
                buttonVariants({
                    variant: 'outline',
                    class: 'w-full justify-start text-left font-normal',
                }),
                "selection:bg-primary dark:bg-input/30 selection:text-primary-foreground border-input ring-offset-background placeholder:text-muted-foreground shadow-xs flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-sm font-medium outline-none transition-[color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
			"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
			"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
                !value && 'text-muted-foreground',
                validationClasses(),
            )}
        >
            <CalendarIcon class="mr-2 size-4" />
            {value ? df.format(value.toDate(getLocalTimeZone())) : 'Pick a date'}
        </Popover.Trigger>
        <Popover.Content class="flex w-auto flex-col space-y-2 p-2" data-dont-unselect="true">
            <div class="rounded-md ">
                <Calendar
                    type="single"
                    {minValue}
                    {maxValue}
                    bind:value={
                        () => value,
                        (v) => {
                            if (v == null) {
                                value = undefined;
                            } else {
                                value = new CalendarDateTime(
                                    v.year,
                                    v.month,
                                    v.day,
                                    v.hour,
                                    v.minute,
                                    v.second,
                                    v?.millisecond,
                                );
                            }
                        }
                    }
                />
            </div>
            <Time
                bind:value={
                    () =>
                        value
                            ? { hour: value?.hour, min: value.minute, sec: value.second }
                            : undefined,
                    (v) => {
                        if (v == null) {
                            value = undefined;
                        } else {
                            const today = new Date();
                            value = new CalendarDateTime(
                                value?.year ?? today.getFullYear(),
                                value?.month ?? today.getMonth() + 1,
                                value?.day ?? today.getDate(),
                                v.hour,
                                v.min,
                                v.sec,
                            );
                        }
                    }
                }
            ></Time>
        </Popover.Content>
    </Popover.Root>
    {#if validation.errorMessage}
        <InputError class="mt-2" message={validation.errorMessage} />
    {:else if error}
        <InputError class="mt-2" message={error} />
    {/if}
</div>
<!--<Debug {value}></Debug>-->
